/**
 * Files in the chat, kept small: the conversation is what's being read, and these
 * sit in it the way an attachment does. Everything opens large on click.
 *
 *   one picture or video  a thumbnail in the file's own shape, as wide as it is
 *   several               a fan with a count, opening as a collage
 *   audio, anything else  one narrow list: a slim player, or a typed file row
 */
import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { Download, ImageOff, Pause, Play } from 'lucide-react';
import { useChatFiles } from '../FileViewer';
import { downloadWorkspaceFile, useFileUrl } from '../fileMedia';
import { fileKind } from '../fileRefs';
import { setLabel } from '../MediaOverlay';
import { formatSize } from '../workspace-format';

export interface ShownFile {
  path: string;
  name: string;
  size?: number;
  contentType?: string;
  caption?: string;
}

type Size = 'regular' | 'small';

/** The most a thumbnail takes, and a pile's square. Work in progress sits smaller than a result. */
const TILE: Record<Size, string> = {
  regular: 'max-h-40 max-w-[260px]',
  small: 'max-h-28 max-w-[200px]',
};
const PILE: Record<Size, string> = { regular: 'h-32 w-32', small: 'h-24 w-24' };
/** Room for a thumbnail that hasn't loaded, so its shape is the only thing that changes. */
const PLACEHOLDER: Record<Size, string> = { regular: 'h-40 w-40', small: 'h-28 w-28' };

const FRAME = 'rounded-md ring-1 ring-black/10';

function mmss(seconds: number): string {
  if (!Number.isFinite(seconds)) return '0:00';
  const s = Math.round(seconds);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function DownloadButton({ file, className }: { file: ShownFile; className: string }) {
  const { threadId } = useChatFiles();
  if (!threadId) return null;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); downloadWorkspaceFile(threadId, file.path); }}
      title="Download"
      aria-label={`Download ${file.name}`}
      className={`flex items-center justify-center rounded-md transition-[opacity,background-color,color] ${className}`}
    >
      <Download className="h-3.5 w-3.5" />
    </button>
  );
}

function PlayMark() {
  return (
    <span className="pointer-events-none absolute left-1/2 top-1/2 flex h-8 w-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-zinc-950/55 text-white backdrop-blur-sm transition-transform group-hover:scale-110 motion-reduce:transition-none">
      <Play className="ml-0.5 h-3.5 w-3.5 fill-current" />
    </span>
  );
}

function GoneLine({ file }: { file: ShownFile }) {
  return (
    <div className="flex max-w-72 items-center gap-2 py-1 text-xs text-zinc-400">
      <ImageOff className="h-3.5 w-3.5 shrink-0" />
      <span className="min-w-0 truncate" title={file.path}>{file.name}</span>
      <span className="shrink-0">is no longer in the workspace.</span>
    </div>
  );
}

/** One picture or video. The frame is the file's own shape; text under it can't widen it. */
function MediaTile({ file, size }: { file: ShownFile; size: Size }) {
  const { threadId, openFile } = useChatFiles();
  const { ref, url, failed, onError } = useFileUrl(threadId, file.path);
  const [duration, setDuration] = useState<number | null>(null);
  const video = fileKind(file.name, file.contentType) === 'video';

  if (failed) return <GoneLine file={file} />;
  return (
    <figure ref={ref as RefObject<HTMLElement>} className="group m-0 w-min max-w-full">
      <div className={`relative w-max max-w-full overflow-hidden bg-zinc-100 ${FRAME}`}>
        <button type="button" onClick={() => openFile(file)} title={file.path} aria-label={`Open ${file.name}`} className="block cursor-zoom-in">
          {!url ? (
            <div className={`animate-pulse ${PLACEHOLDER[size]}`} />
          ) : video ? (
            <video
              src={`${url}#t=0.1`}
              muted
              playsInline
              preload="metadata"
              onError={onError}
              onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
              className={`block h-auto w-auto bg-zinc-900 ${TILE[size]}`}
            />
          ) : (
            <img src={url} alt={file.caption || file.name} loading="lazy" onError={onError} className={`block h-auto min-h-12 w-auto min-w-20 object-cover ${TILE[size]}`} />
          )}
        </button>
        {video && url && <PlayMark />}
        {video && duration !== null && (
          <span className="pointer-events-none absolute bottom-1.5 right-1.5 rounded bg-zinc-950/60 px-1 py-px text-[11px] tabular-nums text-white backdrop-blur-sm">{mmss(duration)}</span>
        )}
        <DownloadButton file={file} className="absolute right-1.5 top-1.5 h-6 w-6 bg-zinc-950/55 text-white opacity-0 backdrop-blur-sm hover:bg-zinc-950/75 focus-visible:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100" />
      </div>
      <figcaption className="mt-1 w-0 min-w-full truncate text-xs text-zinc-500" title={file.path}>{file.caption || file.name}</figcaption>
    </figure>
  );
}

/** A file in the pile: the first frame of a video, a picture otherwise. */
function PileCard({ file, className }: { file: ShownFile; className: string }) {
  const { threadId } = useChatFiles();
  const { ref, url, failed, onError } = useFileUrl(threadId, file.path);
  const video = fileKind(file.name, file.contentType) === 'video';
  return (
    <div
      ref={ref as RefObject<HTMLDivElement>}
      className={`absolute inset-0 overflow-hidden rounded-md bg-zinc-100 shadow-sm ring-2 ring-white transition-transform duration-200 ease-out motion-reduce:transition-none ${className}`}
    >
      {failed ? (
        <div className="flex h-full items-center justify-center text-zinc-300"><ImageOff className="h-4 w-4" /></div>
      ) : !url ? (
        <div className="h-full animate-pulse" />
      ) : video ? (
        <video src={`${url}#t=0.1`} muted playsInline preload="metadata" onError={onError} className="h-full w-full object-cover" />
      ) : (
        <img src={url} alt={file.caption || file.name} onError={onError} className="h-full w-full object-cover" />
      )}
    </div>
  );
}

/** Several pictures or videos as one object: a fan of three, pivoting on the bottom edge, and how many there are. */
function MediaPile({ files, size }: { files: ShownFile[]; size: Size }) {
  const { openCollage } = useChatFiles();
  const [top, second, third] = files;
  const label = setLabel(files);
  const fan = 'origin-bottom';
  return (
    <button
      type="button"
      onClick={() => openCollage(files)}
      title={`Open ${label}`}
      aria-label={`Open ${label}`}
      className={`group relative mx-5 mb-4 mt-3 block shrink-0 cursor-zoom-in ${PILE[size]}`}
    >
      {third && <PileCard file={third} className={`${fan} -rotate-[9deg] group-hover:-rotate-[14deg]`} />}
      {second && <PileCard file={second} className={`${fan} rotate-[9deg] group-hover:rotate-[14deg]`} />}
      <PileCard file={top!} className="shadow-md group-hover:-translate-y-1" />
      <span className="pointer-events-none absolute bottom-1.5 right-1.5 rounded bg-zinc-950/65 px-1.5 py-px text-[11px] font-medium tabular-nums text-white backdrop-blur-sm transition-transform duration-200 group-hover:-translate-y-1 motion-reduce:transition-none">{files.length}</span>
    </button>
  );
}

/** What's playing, so starting one stops the last. */
let playing: HTMLAudioElement | null = null;

function AudioRow({ file }: { file: ShownFile }) {
  const { threadId } = useChatFiles();
  const { ref, url, failed, onError } = useFileUrl(threadId, file.path);
  const audio = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => () => { if (playing === audio.current) playing = null; }, []);

  const toggle = () => {
    const el = audio.current;
    if (!el) return;
    if (!el.paused) { el.pause(); return; }
    if (playing && playing !== el) playing.pause();
    playing = el;
    void el.play();
  };
  const seek = (e: React.MouseEvent<HTMLButtonElement>) => {
    const el = audio.current;
    if (!el || !duration) return;
    const box = e.currentTarget.getBoundingClientRect();
    el.currentTime = ((e.clientX - box.left) / box.width) * duration;
    setTime(el.currentTime);
  };

  return (
    <div ref={ref as RefObject<HTMLDivElement>} className="group flex h-11 items-center gap-2.5 pl-2 pr-2.5">
      <button
        type="button"
        onClick={toggle}
        disabled={!url || failed}
        aria-label={`${isPlaying ? 'Pause' : 'Play'} ${file.name}`}
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-zinc-900 text-white transition-colors hover:bg-zinc-700 disabled:bg-zinc-200"
      >
        {isPlaying ? <Pause className="h-3 w-3 fill-current" /> : <Play className="ml-px h-3 w-3 fill-current" />}
      </button>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="min-w-0 truncate text-xs text-zinc-800" title={file.path}>{file.caption || file.name}</span>
          <span className="shrink-0 text-[11px] tabular-nums text-zinc-400">
            {failed ? 'Gone' : isPlaying || time > 0 ? `${mmss(time)} / ${mmss(duration)}` : mmss(duration)}
          </span>
        </div>
        <button type="button" onClick={seek} tabIndex={-1} aria-hidden className="block w-full cursor-pointer py-1">
          <span className="block h-0.5 overflow-hidden rounded-full bg-zinc-200">
            <span className="block h-full rounded-full bg-accent-500" style={{ width: `${duration ? (time / duration) * 100 : 0}%` }} />
          </span>
        </button>
      </div>
      <DownloadButton file={file} className="h-6 w-6 shrink-0 text-zinc-300 hover:bg-zinc-100 hover:text-zinc-700" />
      {url && (
        <audio
          ref={audio}
          src={url}
          preload="metadata"
          onError={onError}
          onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
          onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => { setIsPlaying(false); setTime(0); }}
        />
      )}
    </div>
  );
}

/** The type is the picture: a PDF reads as PDF faster than as a generic page icon. */
function FileRow({ file }: { file: ShownFile }) {
  const { openFile } = useChatFiles();
  const ext = /\.([a-z0-9]{1,4})$/i.exec(file.name)?.[1] ?? 'file';
  return (
    <div className="group flex h-11 items-center gap-2.5 pl-2 pr-2.5 transition-colors hover:bg-zinc-50">
      <button type="button" onClick={() => openFile(file)} title={file.path} className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 text-left">
        <span className="flex h-7 w-8 shrink-0 items-center justify-center rounded bg-zinc-100 text-[9px] font-semibold uppercase text-zinc-500">{ext}</span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-xs text-zinc-800">{file.caption || file.name}</span>
          {file.size != null && <span className="block truncate text-[11px] text-zinc-400">{formatSize(file.size)}</span>}
        </span>
      </button>
      <DownloadButton file={file} className="h-6 w-6 shrink-0 text-zinc-300 hover:bg-zinc-100 hover:text-zinc-700" />
    </div>
  );
}

export function ShownFiles({ files, missing, size = 'regular', after }: { files: ShownFile[]; missing?: string[]; size?: Size; after?: ReactNode }) {
  const kinds = files.map((file) => ({ file, kind: fileKind(file.name, file.contentType) }));
  const visual = kinds.filter((k) => k.kind === 'image' || k.kind === 'video').map((k) => k.file);
  const listed = kinds.filter((k) => k.kind !== 'image' && k.kind !== 'video');

  return (
    <div className="my-2">
      <div className="flex flex-wrap items-start gap-3">
        {visual.length === 1 && <MediaTile file={visual[0]!} size={size} />}
        {visual.length > 1 && <MediaPile files={visual} size={size} />}
        {listed.length > 0 && (
          <div className="w-72 max-w-full divide-y divide-zinc-100 overflow-hidden rounded-md border border-zinc-200 bg-white">
            {listed.map(({ file, kind }) => (kind === 'audio' ? <AudioRow key={file.path} file={file} /> : <FileRow key={file.path} file={file} />))}
          </div>
        )}
      </div>
      {missing && missing.length > 0 && <div className="mt-1 text-xs text-zinc-400">Not found: {missing.join(', ')}</div>}
      {after}
    </div>
  );
}
